// src/pages/dashboard/ReflectionForm.jsx
import { useEffect } from "react";

export default function ReflectionForm({
  period,
  setPeriod,
  selectedDate,
  setSelectedDate,
  questions,
  answers,
  isLoading,
  polishing,
  submitting,
  updateAnswer,
  addAnswer,
  removeAnswer,
  polishAnswer,
  saveReflection,
}) {
  const today = new Date().toISOString().split("T")[0];

  const getCalendarType = () => {
    if (period === "daily") return "date";
    if (period === "weekly") return "week";
    if (period === "monthly") return "month";
    return "date";
  };

  const getMinValue = () => {
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 2);

    if (period === "daily") return minDate.toISOString().split("T")[0];
    if (period === "weekly") return `${minDate.getFullYear()}-W01`;
    if (period === "monthly") {
      return `${minDate.getFullYear()}-${String(
        minDate.getMonth() + 1
      ).padStart(2, "0")}`;
    }
    return "";
  };

  // 🔒 MAX VALUE → FUTURE BLOCK (DAILY / WEEKLY / MONTHLY)
  const getMaxValue = () => {
    const now = new Date();

    if (period === "daily") {
      return now.toISOString().split("T")[0];
    }

    if (period === "weekly") {
      const year = now.getFullYear();
      const week = Math.ceil(
        (((now - new Date(year, 0, 1)) / 86400000) +
          new Date(year, 0, 1).getDay() +
          1) / 7
      );
      return `${year}-W${String(week).padStart(2, "0")}`;
    }

    if (period === "monthly") {
      return `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}`;
    }

    return undefined;
  };

  // Auto-update default date when period changes
  useEffect(() => {
    const getDefault = () => {
      const now = new Date();

      if (period === "daily") return today;

      if (period === "weekly") {
        const year = now.getFullYear();
        const week = Math.ceil(
          (((now - new Date(year, 0, 1)) / 86400000) +
            new Date(year, 0, 1).getDay() +
            1) / 7
        );
        return `${year}-W${String(week).padStart(2, "0")}`;
      }

      if (period === "monthly") {
        return `${now.getFullYear()}-${String(
          now.getMonth() + 1
        ).padStart(2, "0")}`;
      }

      return "";
    };

    const defaultDate = getDefault();
    if (!selectedDate || selectedDate === defaultDate) {
      setSelectedDate(defaultDate);
    }
  }, [period]);

  return (
    <div className="diamond-card">
      <div className="card-header-ultra">
        <h2 className="card-title-ultra">
          {period.charAt(0).toUpperCase() + period.slice(1)} Reflection Form
        </h2>
        <p className="card-subtitle-ultra">
          Add multiple points per question and polish using AI.
        </p>
      </div>

      <div className="card-body-ultra">
        <div className="period-selector-ultra">
          {/* PERIOD TYPE */}
          <div className="period-group-ultra">
            <label className="period-label-ultra">
              <span className="period-icon">📅</span>
              Period Type
            </label>
            <select
              className="period-select-ultra"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {/* DATE PICKER */}
          <div className="period-group-ultra">
            <label className="period-label-ultra">
              <span className="period-icon">📆</span>
              {period.charAt(0).toUpperCase() + period.slice(1)} Date
            </label>

            <div
              className="date-input-wrapper"
              style={{ cursor: "pointer" }}
              onClick={() =>
                document.getElementById("globalDatePicker")?.showPicker?.()
              }
            >
              <input
                id="globalDatePicker"
                type={getCalendarType()}
                className="period-select-ultra date-input"
                value={selectedDate}
                min={getMinValue()}
                max={getMaxValue()}
                onChange={(e) => {
                  if (e.target.value > getMaxValue()) return;
                  setSelectedDate(e.target.value);
                }}
                style={{
                  paddingLeft: "50px",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%2300d4ff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "16px center",
                  backgroundSize: "20px",
                }}
              />
            </div>
          </div>
        </div>

        {/* QUESTIONS */}
        <div className="questions-container">
          {isLoading ? (
            <div className="loading-text-ultra">Loading questions...</div>
          ) : questions.length === 0 ? (
            <div className="empty-state-ultra">
              No questions found for this period.
            </div>
          ) : (
            questions.map((q, qi) => (
              <div key={qi} className="question-block-ultra">
                <div className="question-header-ultra">
                  <div className="question-number-ultra">{qi + 1}</div>
                  <div className="question-text-ultra">{q}</div>
                </div>

                <div className="answer-inputs-ultra">
                  {answers[qi]?.map((ans, ai) => {
                    const key = `${qi}-${ai}`;
                    return (
                      <div key={ai} className="answer-row-ultra">
                        <input
                          className="answer-input-ultra"
                          placeholder="Type your point here..."
                          value={ans}
                          onChange={(e) =>
                            updateAnswer(qi, ai, e.target.value)
                          }
                        />
                        {answers[qi].length > 1 && (
                          <button
                            className="remove-point-btn-ultra"
                            onClick={() => removeAnswer(qi, ai)}
                          >
                            ✖
                          </button>
                        )}
                        <button
                          className="ai-btn-ultra"
                          onClick={() => polishAnswer(qi, ai)}
                          disabled={!ans.trim() || polishing[key]}
                        >
                          {polishing[key]
                            ? "🌀 Polishing..."
                            : "✨ AI Polish"}
                        </button>
                      </div>
                    );
                  })}

                  <button
                    className="add-point-btn-ultra"
                    onClick={() => addAnswer(qi)}
                  >
                    Add another point
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="form-footer-ultra">
        <button
          className="submit-btn-ultra"
          onClick={saveReflection}
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Submit Reflection"}
        </button>
      </div>
    </div>
  );
}
